import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, Shield, Clock, Globe, UserCheck } from 'lucide-react';

const PrivateCharterPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const advantages = [
    { icon: <Shield className="w-6 h-6" />, title: 'Privacy & Security', desc: 'Complete confidentiality with private terminals and discreet service.' },
    { icon: <Clock className="w-6 h-6" />, title: 'Schedule Flexibility', desc: 'Depart on your timeline. No waiting, no delays, no connections.' },
    { icon: <Globe className="w-6 h-6" />, title: 'Global Access', desc: 'Reach 10,000+ airports worldwide, including private airfields.' },
    { icon: <UserCheck className="w-6 h-6" />, title: 'Personalized Service', desc: 'Tailored catering, ground transport, and concierge arrangements.' },
  ];

  return (
    <div data-testid="private-charter-page" className="bg-white">
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/pc01.jpg"
            alt="Private jet"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay"></div>
        </div>

        <div className="relative z-10 container-padding w-full pt-32 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="inline-block text-blue-700 text-sm font-medium tracking-widest uppercase mb-6">
              Our Services
            </span>
            <h1 
              data-testid="service-headline"
              className="text-4xl sm:text-5xl lg:text-6xl font-medium text-white leading-tight mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Private Charter
            </h1>
            <p className="text-lg text-white/80 font-light leading-relaxed max-w-2xl mb-8">
              Exclusive aircraft charter for business or pleasure.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                data-testid="hero-enquiry-btn"
                className="inline-flex items-center justify-center gap-2 bg-red-600 text-white px-8 py-4 text-sm font-medium tracking-wider uppercase hover:bg-red-700 transition-all duration-300"
              >
                Request Quote
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="tel:+6581888023"
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm text-white border border-white/30 px-8 py-4 text-sm font-medium tracking-wider uppercase hover:bg-white/20 transition-all duration-300"
              >
                <Phone className="w-4 h-4" />
                +65 8188 8023
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-gap">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 
                className="heading-lg text-slate-900 mb-6"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Fly on Your Terms
              </h2>
              <div className="gold-line mb-8"></div>
              <p className="body-large mb-6">
                With Singapore Air Charter, you'll truly go above and beyond. And we have the experience to get you there.
              </p>
              <p className="body-default mb-6">
                Business or pleasure — whatever your requirements, Singapore Air Charter presents available options and tailors a service that exceeds them. From initial enquiry to an aircraft that's perfectly suited to your mission profile, Singapore Air Charter will provide a discreet, attentive and dependable service.
              </p>
              <p className="body-default mb-8">
                We're bespoke private air travel backed by a dedicated team who love what they do. Allow Singapore Air Charter to provide local and global solutions to your private air travel requirements.
              </p>
              <Link
                to="/contact"
                data-testid="content-cta-btn"
                className="inline-flex items-center gap-2 bg-slate-900 text-white px-8 py-4 text-sm font-medium tracking-wider uppercase hover:bg-slate-800 transition-all duration-300"
              >
                Get Started
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <img
                src="/ci001.jpeg"
                alt="Private jet interior"
                className="w-full h-[500px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Private Advantage */}
      <section className="section-gap bg-slate-50">
        <div className="container-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="caption-text text-blue-700 mb-4 block">Benefits</span>
            <h2 
              className="heading-lg text-slate-900 mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              The Private Advantage
            </h2>
            <div className="gold-line mx-auto"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {advantages.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 text-center border border-slate-100"
              >
                <div className="text-blue-700 mb-4 flex justify-center">{item.icon}</div>
                <h3 className="text-lg font-medium text-slate-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-gap">
        <div className="container-padding text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 
              className="heading-lg text-slate-900 mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Ready to Experience Private Travel?
            </h2>
            <p className="body-large mb-10 max-w-2xl mx-auto">
              Contact our team for a personalized charter quote. Available 24/7.
            </p>
            <Link
              to="/contact"
              data-testid="final-cta-btn"
              className="inline-flex items-center justify-center gap-2 bg-red-600 text-white px-10 py-4 text-sm font-medium tracking-wider uppercase hover:bg-red-700 transition-all duration-300"
            >
              Charter Enquiry
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PrivateCharterPage;
